(() => {
  'use strict';

  const CUSTOM_VALUE = '__custom__';
  const waitForConfigurator = (root) => new Promise((resolve, reject) => {
    let attempts = 0;
    const inspect = () => {
      const ready = root.dataset.srdInitialized === 'true'
        && root.querySelector('[data-srd-size]')?.options.length
        && root.querySelector('[data-srd-label-preset]')?.options.length;
      if (ready) return resolve();
      attempts += 1;
      if (attempts > 200) return reject(new Error('Base configurator did not initialize'));
      window.setTimeout(inspect, 25);
    };
    inspect();
  });

  const setProperty = (root, name, value) => {
    const input = root.querySelector(`[data-srd-property="${name}"]`);
    if (input) input.value = value == null ? '' : String(value);
  };

  const parseJson = (value, fallback = {}) => {
    try { return JSON.parse(value || ''); }
    catch (_) { return fallback; }
  };

  const selectedValueExists = (select, value) => Array.from(select.options).some((option) => option.value === value);
  const yesNo = (value) => value ? 'Required' : 'Not Required';

  async function enhance(root) {
    if (root.dataset.srdProductionInitialized === 'true') return;
    root.dataset.srdProductionInitialized = 'true';
    await waitForConfigurator(root);

    const standards = await fetch(root.dataset.standardsUrl, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error('Industrial standards unavailable');
      return response.json();
    });
    const presets = standards.product_families.pipe_conduit_labels.presets
      .filter((preset) => preset.active)
      .sort((a, b) => a.display_order - b.display_order);
    const presetByLegend = new Map(presets.map((preset) => [preset.legend, preset]));
    const searchIndex = presets.map((preset) => ({
      preset,
      searchable: `${preset.legend} ${preset.category} ${preset.industry_standard || ''}`.toLowerCase()
    }));

    const elements = {
      search: root.querySelector('[data-srd-label-search]'),
      preset: root.querySelector('[data-srd-label-preset]'),
      result: root.querySelector('[data-srd-search-result]'),
      customText: root.querySelector('[data-srd-custom-text]'),
      size: root.querySelector('[data-srd-size]'),
      material: root.querySelector('[data-srd-material]'),
      lamination: root.querySelector('[data-srd-lamination]'),
      standardColors: root.querySelector('[data-srd-standard-colors]'),
      background: root.querySelector('[data-srd-background-color]'),
      text: root.querySelector('[data-srd-text-color]'),
      quantity: root.querySelector('[data-srd-quantity]'),
      form: root.querySelector('form[data-type="add-to-cart-form"]')
    };
    const normalizeQuantity = () => {
      const size = standards.sizes[elements.size.value];
      if (!size) return 0;
      const minimum = Number(size.minimum_quantity);
      const increment = Number(size.quantity_increment);
      let quantity = Number.parseInt(elements.quantity.value, 10);
      if (!Number.isFinite(quantity) || quantity < minimum) quantity = minimum;
      quantity = minimum + Math.round((quantity - minimum) / increment) * increment;
      elements.quantity.min = String(minimum);
      elements.quantity.step = String(increment);
      elements.quantity.value = String(quantity);
      return quantity;
    };

    const syncProduction = () => {
      const quantity = normalizeQuantity();
      const size = standards.sizes[elements.size.value];
      if (!size) return;
      const selectedPreset = presetByLegend.get(elements.preset.value);
      const category = elements.preset.value === CUSTOM_VALUE ? 'Custom' : selectedPreset?.category || '';
      const privateConfiguration = root.querySelector('[data-srd-property="_Configuration JSON"]');
      const configuration = {
        ...parseJson(privateConfiguration?.value),
        category,
        quantity,
        labels_per_sheet: size.labels_per_sheet,
        minimum_quantity: size.minimum_quantity,
        quantity_increment: size.quantity_increment,
        pricing_group: size.pricing_group,
        shipping_group: size.shipping_group,
        requires_transfer_tape: Boolean(size.requires_transfer_tape),
        requires_roll_shipping: Boolean(size.requires_roll_shipping)
      };
      const profile = [
        size.label,
        `${size.labels_per_sheet} per sheet`,
        `minimum ${size.minimum_quantity}`,
        `increment ${size.quantity_increment}`,
        `transfer tape ${yesNo(size.requires_transfer_tape)}`,
        `roll shipping ${yesNo(size.requires_roll_shipping)}`
      ].join(' | ');

      setProperty(root, '_Category', category);
      setProperty(root, 'Background', configuration.background_color);
      setProperty(root, 'Text', configuration.text_color);
      setProperty(root, 'Arrow', configuration.arrow_direction.charAt(0).toUpperCase() + configuration.arrow_direction.slice(1));
      setProperty(root, '_Size', configuration.label_size);
      setProperty(root, 'Labels Included', quantity);
      setProperty(root, 'Transfer Tape', size.requires_transfer_tape ? 'Included' : '');
      setProperty(root, '_Manufacturing Profile', profile);
      setProperty(root, '_Pricing Group', size.pricing_group);
      setProperty(root, '_Shipping Group', size.shipping_group);
      setProperty(root, '_Labels Per Sheet', size.labels_per_sheet);
      setProperty(root, '_Minimum Quantity', size.minimum_quantity);
      setProperty(root, '_Quantity Increment', size.quantity_increment);
      setProperty(root, '_Requires Transfer Tape', yesNo(size.requires_transfer_tape));
      setProperty(root, '_Requires Roll Shipping', yesNo(size.requires_roll_shipping));
      setProperty(root, '_Configuration JSON', JSON.stringify(configuration));
      root.dataset.srdManufacturingProfile = size.pricing_group;
      root.dataset.srdShippingGroup = size.shipping_group;

    };

    const optimizedSearch = elements.search.cloneNode(true);
    elements.search.replaceWith(optimizedSearch);
    elements.search = optimizedSearch;
    elements.search.addEventListener('input', () => {
      const query = elements.search.value.trim().toLowerCase();
      const selected = elements.preset.value;
      const matches = query ? searchIndex.filter((entry) => entry.searchable.includes(query)) : searchIndex;
      const fragment = document.createDocumentFragment();
      matches.forEach(({ preset }) => fragment.append(new Option(`${preset.legend} — ${preset.category}`, preset.legend)));
      fragment.append(new Option('CUSTOM LABEL...', CUSTOM_VALUE));
      elements.preset.replaceChildren(fragment);
      if (selectedValueExists(elements.preset, selected)) elements.preset.value = selected;
      else elements.preset.value = matches[0]?.preset.legend || CUSTOM_VALUE;
      elements.result.textContent = query
        ? `${matches.length} preset${matches.length === 1 ? '' : 's'} found. Custom label remains available.`
        : `${matches.length} industrial presets available.`;
      elements.preset.dispatchEvent(new Event('change', { bubbles: true }));
    });

    syncProduction();
    root.addEventListener('change', () => window.queueMicrotask(syncProduction));
    elements.customText.addEventListener('input', () => window.queueMicrotask(syncProduction));
    elements.quantity.addEventListener('blur', () => syncProduction());
    elements.quantity.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') syncProduction();
    });
    root.querySelectorAll('[data-srd-quantity-minus],[data-srd-quantity-plus]')
      .forEach((button) => button.addEventListener('click', () => window.queueMicrotask(syncProduction)));
    elements.form?.addEventListener('submit', () => syncProduction(), { capture: true });
  }

  const start = () => document.querySelectorAll('[data-srd-industrial-label]').forEach((root) => {
    enhance(root).catch((error) => console.error('SRD production completion:', error));
  });

  document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', start);
})();
